import type { ReactElement } from 'react';
import Plan, { PlanType } from 'models/Plan';
import { useUIDSeed } from 'react-uid';

type Props = {
    isPlanReloading: boolean;
    hasGift: boolean;
    currentPlanType: PlanType;
    currentPlan: Plan;
    setServings: (servings) => void;
    setExtras: (extraDinners) => void;
    onModalOpen?: () => void;
    setShowMealPreferences: (bool) => void;
};

const ServingSelection = ({
  isPlanReloading,
  hasGift,
  currentPlanType,
  currentPlan,
  setServings,
  setExtras,
  onModalOpen = () => null,
  setShowMealPreferences,
}: Props): ReactElement => {
  const seed = useUIDSeed();

  const servingOptions = {
    [PlanType.MEAL_KIT]: Plan.getServingOptions(PlanType.MEAL_KIT, hasGift),
    [PlanType.PREPARED]: Plan.getServingOptions(PlanType.PREPARED, hasGift),
  };

  const extraDinnerOptions = {
    [PlanType.MEAL_KIT]: Plan.getExtraDinnerOptions(PlanType.MEAL_KIT, hasGift),
    [PlanType.PREPARED]: Plan.getExtraDinnerOptions(PlanType.PREPARED, hasGift),
  };

  return (
    <>
      <fieldset>
        <div className="c-plan__servings">
          <legend id="plan-servings">
            Number of servings per meal:
          </legend>
          <ul
            role="radiogroup"
            className={[
              'c-switch',
              isPlanReloading ? 'disabled' : '',
            ].join(' ')}
            aria-labelledby="plan-servings"
          >
            {servingOptions[currentPlanType].map((servings) => (
              <li key={currentPlan.name + seed(servings)}>
                <button
                  type="button"
                  role="radio"
                  aria-label={`Select ${servings} Serving`}
                  aria-checked={currentPlan.servings === servings}
                  className={currentPlan.servings === servings
                    ? 'is-selected'
                    : ''}
                  onClick={() => setServings(servings)}
                >
                  {servings}
                  <span className="sr-only">serving</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </fieldset>
      <hr />
      <fieldset>
        <div className="c-plan__dinners">
          <legend id="plan-servings">
            Meals per week:
          </legend>
          <ul
            role="radiogroup"
            className={[
              'c-switch',
              isPlanReloading ? 'disabled' : '',
            ].join(' ')}
            aria-labelledby="plan-servings"
          >
            {extraDinnerOptions[currentPlanType].map((extraDinners) => (
              <li key={seed(extraDinners)}>
                <button
                  type="button"
                  role="radio"
                  aria-label={
                        `Select ${
                          currentPlan.includedDinners + extraDinners
                        } Meals`
                    }
                  aria-checked={
                        currentPlan.extraDinners === extraDinners
                    }
                  className={
                        currentPlan.extraDinners === extraDinners
                          ? 'is-selected'
                          : ''
                    }
                  onClick={() => setExtras(extraDinners)}
                >
                  {currentPlan.includedDinners + extraDinners}
                  <span className="sr-only">serving</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </fieldset>
      {currentPlan.servings === 2 && !hasGift && (
        <>
          <hr />
          <span>
            Meal Preferences:
            <br />
            {` ${currentPlan.mealPreferences}`}
          </span>
          <button
            type="button"
            className="c-link float-right"
            onClick={() => {
              onModalOpen();
              setShowMealPreferences(true);
            }}
          >
            Change
          </button>
        </>
      )}
    </>
  );
};

export default ServingSelection;
